// Shared fetch helper for the lawyer backend: REACT_APP_API_URL as the base, a
// Firebase ID token as Bearer auth, and FastAPI's `detail` as the error message.

export class ApiError extends Error {
  status: number;
  data: any;

  constructor(message: string, status: number, data: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const apiUrl = (path: string) => `${process.env.REACT_APP_API_URL}${path}`;

// FastAPI puts a string in `detail` for HTTPException, an object when the
// route passes one (the 409 duplicate office), and a list for validation
// errors. Turn any of those into one readable line.
export const errorMessageFrom = (data: any, fallback: string): string => {
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && typeof detail.message === 'string') {
    return detail.message;
  }
  if (Array.isArray(detail) && detail.length > 0 && typeof detail[0]?.msg === 'string') {
    return detail[0].msg;
  }
  return fallback;
};

const readJson = async (response: Response): Promise<any> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export const request = async (
  token: string,
  path: string,
  init: RequestInit,
  fallbackError: string
): Promise<any> => {
  // Merge caller headers (any HeadersInit shape); the auth header always wins.
  const callerHeaders: Record<string, string> = {};
  new Headers(init.headers).forEach((value, key) => {
    if (key !== 'authorization') callerHeaders[key] = value;
  });
  const response = await fetch(apiUrl(path), {
    ...init,
    headers: {
      ...(init.body && !('content-type' in callerHeaders) ? { 'Content-Type': 'application/json' } : {}),
      ...callerHeaders,
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new ApiError(errorMessageFrom(data, fallbackError), response.status, data);
  }
  return data;
};

// request() for a binary GET (a document preview image) instead of JSON.
export const requestBlob = async (token: string, path: string, fallbackError: string): Promise<Blob> => {
  const response = await fetch(apiUrl(path), {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const data = await readJson(response);
    throw new ApiError(errorMessageFrom(data, fallbackError), response.status, data);
  }
  return response.blob();
};
